import { type Ref, useState } from "react";

import { Column, EmptyState } from "@hightouch/htui";

import { useMapperListbox } from "src/formkit/components/mapper-v2/hooks/use-mapper-listbox";
import {
  MapperList,
  MapperListRow,
  MapperListSection,
  MapperRowIcon,
  MapperSearch,
} from "src/formkit/components/mapper-v2/mapper-list";
import {
  getValueType,
  type MapperValue,
} from "src/formkit/components/mapper-v2/mapper-selection";
import {
  getVariableSections,
  type VariableOption,
} from "src/formkit/components/mapper-v2/variable-options";
import { MappingType, type VariableScope } from "src/formkit/components/types";
import { NEW_STATIC_ICONS } from "src/formkit/components/utils";
import { getSearchRegExp } from "src/utils/string";

type VariablePaneProps = {
  scope: VariableScope;
  /** The committed mapping value, whose variable is shown as chosen. */
  value: MapperValue;
  searchRef: Ref<HTMLInputElement>;
  onChoose: (value: MapperValue) => void;
  onClose: () => void;
};

/** Values Hightouch fills in at sync time, grouped by what they describe. */
export function VariablePane({
  scope,
  value,
  searchRef,
  onChoose,
  onClose,
}: VariablePaneProps) {
  const [search, setSearch] = useState("");
  const committedVariable =
    getValueType(value) === MappingType.VARIABLE ? value.variable : undefined;

  const trimmedSearch = search.trim();
  const regex = trimmedSearch ? getSearchRegExp(trimmedSearch, "i") : undefined;
  const sections = getVariableSections(scope).flatMap((section) => {
    const options = regex
      ? section.options.filter((option) => regex.test(option.label))
      : section.options;
    return options.length ? [{ ...section, options }] : [];
  });
  const options = sections.flatMap((section) => section.options);

  const chooseOption = (option: VariableOption) => {
    if (option.variable === committedVariable) {
      onClose();
    } else {
      onChoose({ type: MappingType.VARIABLE, variable: option.variable });
    }
  };

  const listbox = useMapperListbox({
    optionCount: options.length,
    hasSearch: Boolean(trimmedSearch),
    onChoose: (index) => {
      const option = options[index];
      if (option) chooseOption(option);
    },
  });

  const searchLabel =
    scope === "realtime-journey" ? "Search journey values" : "Search variables";

  let optionIndex = 0;
  return (
    <>
      <MapperSearch
        inputRef={searchRef}
        label={searchLabel}
        listbox={listbox}
        placeholder={`${searchLabel}…`}
        value={search}
        onChange={(nextSearch) => {
          setSearch(nextSearch);
          listbox.resetActiveIndex();
        }}
      />
      {options.length > 0 ? (
        <MapperList
          activeOptionId={listbox.activeOptionId}
          label={searchLabel}
          listId={listbox.listId}
          scrollRef={listbox.scrollRef}
        >
          {sections.map((section) => (
            <MapperListSection key={section.label} label={section.label}>
              {section.options.map((option) => {
                const index = optionIndex++;
                return (
                  <MapperListRow
                    key={option.variable}
                    description={option.description}
                    hasDescriptionBelow
                    icon={
                      <MapperRowIcon icon={NEW_STATIC_ICONS[option.type]} />
                    }
                    id={listbox.getOptionId(index)}
                    isActive={index === listbox.activeIndex}
                    isSelected={option.variable === committedVariable}
                    label={option.label}
                    onChoose={() => chooseOption(option)}
                  />
                );
              })}
            </MapperListSection>
          ))}
        </MapperList>
      ) : (
        <Column flex={1} justifyContent="center" p={6}>
          <EmptyState
            title={`No ${scope === "realtime-journey" ? "journey values" : "variables"} match your search`}
            variant="minimal"
          />
        </Column>
      )}
    </>
  );
}
